import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, CheckCheck, Target, FileText, CheckCircle, Gift, Package, Bell } from 'lucide-react';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import type { Notification } from '@crewcast/shared';
import '../styles/notifications.css';

interface NotificationPanelProps {
  onClose: () => void;
  onUnreadCountChange: (count: number) => void;
}

function formatRelativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(isoString).toLocaleDateString();
}

function getNotificationRoute(notification: Notification): string {
  const { type, metadata } = notification;
  switch (type) {
    case 'MOMENT_LAUNCHED':
      return '/moments';
    case 'SUBMISSION_RECEIVED':
      return '/review-submissions';
    case 'SUBMISSION_APPROVED':
      return '/submissions';
    case 'REWARD_CLAIMED':
      return '/admin/claimed-rewards';
    case 'REWARD_FULFILLED':
      return '/claimed-rewards';
    default:
      return '/dashboard';
  }
}

function NotificationIcon({ type }: { type: string }) {
  const props = { size: 16 };
  switch (type) {
    case 'MOMENT_LAUNCHED': return <Target {...props} />;
    case 'SUBMISSION_RECEIVED': return <FileText {...props} />;
    case 'SUBMISSION_APPROVED': return <CheckCircle {...props} />;
    case 'REWARD_CLAIMED': return <Gift {...props} />;
    case 'REWARD_FULFILLED': return <Package {...props} />;
    default: return <Bell {...props} />;
  }
}

export default function NotificationPanel({ onClose, onUnreadCountChange }: NotificationPanelProps) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isMarkingAll, setIsMarkingAll] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const fetchNotifications = useCallback(async () => {
    try {
      if (!token) return;

      const response = await fetch(`${authConfig.apiUrl}/api/notifications?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications ?? []);
        setNextCursor(data.nextCursor ?? null);
        const unread = (data.notifications ?? []).filter((n: Notification) => !n.read).length;
        onUnreadCountChange(unread);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, [onUnreadCountChange]);

  const loadMore = async () => {
    if (!nextCursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      if (!token) return;

      const response = await fetch(
        `${authConfig.apiUrl}/api/notifications?limit=20&cursor=${encodeURIComponent(nextCursor)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setNotifications((prev) => [...prev, ...(data.notifications ?? [])]);
        setNextCursor(data.nextCursor ?? null);
      }
    } catch (err) {
      console.error('Failed to load more notifications:', err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      // Optimistic update
      setNotifications((prev) =>
        prev.map((n) => (n.notificationId === notification.notificationId ? { ...n, read: true } : n))
      );
      onUnreadCountChange(
        notifications.filter((n) => !n.read && n.notificationId !== notification.notificationId).length
      );

      try {
          await fetch(`${authConfig.apiUrl}/api/notifications/${notification.notificationId}/read`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    }

    onClose();
    navigate(getNotificationRoute(notification));
  };

  const handleMarkAllRead = async () => {
    if (isMarkingAll) return;
    setIsMarkingAll(true);

    // Optimistic update
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    onUnreadCountChange(0);

    try {
      await fetch(`${authConfig.apiUrl}/api/notifications/read-all`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      // Re-fetch to restore accurate state on failure
      fetchNotifications();
    } finally {
      setIsMarkingAll(false);
    }
  };

  const hasUnread = notifications.some((n) => !n.read);

  return (
    <>
      <div className="notification-overlay" onClick={onClose} />
      <div className="notification-panel">
        <div className="notification-panel-header">
          <h3 className="notification-panel-title">Notifications</h3>
          <div className="notification-panel-actions">
            {hasUnread && (
              <button
                className="notification-mark-all-btn"
                onClick={handleMarkAllRead}
                disabled={isMarkingAll}
                title="Mark all as read"
              >
                <CheckCheck size={16} />
                <span>Mark all read</span>
              </button>
            )}
            <button className="notification-close-btn" onClick={onClose} aria-label="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="notification-list">
          {isLoading ? (
            <div className="notification-empty">
              <div className="notification-loading-spinner" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="notification-empty">
              <Bell size={32} className="notification-empty-icon" />
              <p>You're all caught up!</p>
            </div>
          ) : (
            <>
              {notifications.map((notification) => (
                <button
                  key={notification.notificationId}
                  className={`notification-item ${!notification.read ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-item-icon">
                    <NotificationIcon type={notification.type} />
                  </div>
                  <div className="notification-item-body">
                    <div className="notification-item-title">{notification.title}</div>
                    <div className="notification-item-text">{notification.body}</div>
                    <div className="notification-item-time">
                      {formatRelativeTime(notification.createdAt)}
                    </div>
                  </div>
                  {!notification.read && <div className="notification-unread-dot" />}
                </button>
              ))}
              {nextCursor && (
                <button
                  className="notification-load-more-btn"
                  onClick={loadMore}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? <div className="notification-loading-spinner" /> : 'Load more'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
